import { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { 
  FileText, 
  Download, 
  CheckSquare, 
  Loader2, 
  FileJson,
  FileSpreadsheet,
  Code,
  Printer,
  Eye
} from 'lucide-react';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface ImplementationDocumentationProps {
  companyId: string;
  companyName: string;
  accessToken: string;
}

type ExportFormat = 'markdown' | 'html' | 'json' | 'csv';

export function ImplementationDocumentation({
  companyId,
  companyName,
  accessToken
}: ImplementationDocumentationProps) {
  const [selectedVendor, setSelectedVendor] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('markdown');
  const [loading, setLoading] = useState(false);
  const [documentation, setDocumentation] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [checklistData, setChecklistData] = useState<any>(null);

  const handleGenerateDocumentation = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/generate-implementation-guide/${companyId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            vendor: selectedVendor || undefined,
            format: selectedFormat
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        setDocumentation(result.documentation);
        setPreviewMode(true);
      } else {
        const error = await response.json();
        alert('Failed to generate documentation: ' + (error.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error generating documentation:', error);
      alert('Failed to generate documentation');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!documentation) return;

    const blob = new Blob([documentation], { 
      type: getContentType(selectedFormat) 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${companyName.replace(/\s+/g, '_')}_implementation_guide.${getFileExtension(selectedFormat)}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!documentation) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    if (selectedFormat === 'html') {
      printWindow.document.write(documentation);
    } else {
      printWindow.document.write(`
        <html>
          <head>
            <title>${companyName} - Implementation Guide</title>
            <style>
              body { font-family: monospace; white-space: pre-wrap; padding: 20px; }
            </style>
          </head>
          <body>${documentation}</body>
        </html>
      `);
    }
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleLoadChecklist = async () => {
    try {
      setLoading(true);
      const vendorParam = selectedVendor ? `?vendor=${selectedVendor}` : '';
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/implementation-checklist/${companyId}${vendorParam}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setChecklistData(result);
      } else {
        const error = await response.json();
        alert('Failed to load checklist: ' + (error.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error loading checklist:', error);
      alert('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportFormat: 'csv' | 'json') => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/export-configuration/${companyId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            vendor: selectedVendor || undefined,
            format: exportFormat
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        
        // Download the exported data
        const blob = new Blob([result.data], { 
          type: result.contentType 
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${companyName.replace(/\s+/g, '_')}_configuration.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        const error = await response.json();
        alert('Failed to export configuration: ' + (error.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error exporting configuration:', error);
      alert('Failed to export configuration');
    } finally {
      setLoading(false);
    }
  };

  const getContentType = (format: ExportFormat): string => {
    const types: Record<ExportFormat, string> = {
      markdown: 'text/markdown',
      html: 'text/html',
      json: 'application/json',
      csv: 'text/csv'
    };
    return types[format];
  };

  const getFileExtension = (format: ExportFormat): string => {
    const extensions: Record<ExportFormat, string> = {
      markdown: 'md',
      html: 'html',
      json: 'json',
      csv: 'csv'
    };
    return extensions[format];
  };

  return (
    <div className="space-y-6">
      {/* Documentation Generator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Implementation Documentation
          </CardTitle>
          <CardDescription>
            Generate comprehensive implementation guides from your configuration data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Platform Filter</label>
              <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                <SelectTrigger>
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Platforms</SelectItem>
                  <SelectItem value="jira">Jira/JSM</SelectItem>
                  <SelectItem value="servicenow">ServiceNow</SelectItem>
                  <SelectItem value="zendesk">Zendesk</SelectItem>
                  <SelectItem value="ivanti-neurons">Ivanti Neurons</SelectItem>
                  <SelectItem value="bmc-helix">BMC Helix</SelectItem>
                  <SelectItem value="freshservice">Freshservice</SelectItem>
                  <SelectItem value="custom">Custom ITSM</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Output Format</label>
              <Select value={selectedFormat} onValueChange={(v) => setSelectedFormat(v as ExportFormat)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="markdown">
                    <div className="flex items-center gap-2">
                      <Code className="h-4 w-4" />
                      Markdown (.md)
                    </div>
                  </SelectItem>
                  <SelectItem value="html">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      HTML (.html)
                    </div>
                  </SelectItem>
                  <SelectItem value="json">
                    <div className="flex items-center gap-2">
                      <FileJson className="h-4 w-4" />
                      JSON (.json)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              onClick={handleGenerateDocumentation}
              disabled={loading}
              className="flex-1"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Documentation
                </>
              )}
            </Button>

            {documentation && (
              <>
                <Button 
                  onClick={handleDownload}
                  variant="outline"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>

                {selectedFormat === 'html' && (
                  <Button 
                    onClick={handlePrint}
                    variant="outline"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                )}

                <Button 
                  onClick={() => setPreviewMode(!previewMode)}
                  variant="outline"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {previewMode ? 'Hide' : 'Show'} Preview
                </Button>
              </>
            )}
          </div>

          {/* Preview */}
          {previewMode && documentation && (
            <div className="border rounded-lg p-4 bg-muted/50 max-h-96 overflow-y-auto">
              {selectedFormat === 'html' ? (
                <div dangerouslySetInnerHTML={{ __html: documentation }} />
              ) : (
                <pre className="text-sm whitespace-pre-wrap">{documentation}</pre>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Export */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Quick Export
          </CardTitle>
          <CardDescription>
            Export configuration data to CSV or JSON format
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Button 
              onClick={() => handleExport('csv')}
              disabled={loading}
              variant="outline"
              className="flex-1"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4 mr-2" />
              )}
              Export to CSV
            </Button>

            <Button 
              onClick={() => handleExport('json')}
              disabled={loading}
              variant="outline"
              className="flex-1"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileJson className="h-4 w-4 mr-2" />
              )}
              Export to JSON
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Implementation Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5" />
            Implementation Checklist
          </CardTitle>
          <CardDescription>
            Track implementation progress across all configuration items
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={handleLoadChecklist}
            disabled={loading}
            variant="outline"
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <CheckSquare className="h-4 w-4 mr-2" />
                Load Checklist
              </>
            )}
          </Button>

          {checklistData && (
            <>
              {/* Summary */}
              <Alert>
                <AlertDescription>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold">{checklistData.summary.total}</div>
                      <div className="text-xs text-muted-foreground">Total Items</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{checklistData.summary.completed}</div>
                      <div className="text-xs text-muted-foreground">Completed</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-yellow-600">{checklistData.summary.inProgress}</div>
                      <div className="text-xs text-muted-foreground">In Progress</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{checklistData.summary.readyForImplementation}</div>
                      <div className="text-xs text-muted-foreground">Ready</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-600">{checklistData.summary.notStarted}</div>
                      <div className="text-xs text-muted-foreground">Not Started</div>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>

              {/* Checklist Items */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {checklistData.checklist.map((item: any, index: number) => (
                  <div 
                    key={item.id || index}
                    className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={item.completed}
                      readOnly
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{item.name}</span>
                        <Badge variant={item.completed ? "default" : "outline"} className="text-xs">
                          {item.status}
                        </Badge>
                      </div>
                      {item.description && (
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
